import React, { useEffect, useId, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import {
    PREVIEW_LOCK_NOTE_ID,
    usePreviewLock,
} from '@/shared/hooks/usePreviewLock';

export const TAKE_BACK_REASON_MAX = 500;

/**
 * The date a student has to answer an offer. The value is a calendar day,
 * YYYY-MM-DD; the server ends that day at 11:59 PM Mountain time and checks
 * the range, so `min` and `max` only steer the date picker. Empty means the
 * program's own number of days.
 */
export function AnswerByField({
    id,
    value,
    onChange,
    min,
    max,
    disabled = false,
    emptyMeans = "Leave it empty to use the program's usual number of days.",
}) {
    const helpId = `${id}-help`;
    return (
        <Form.Group controlId={id} className="mb-3">
            <Form.Label>Answer by</Form.Label>
            <Form.Control
                type="date"
                value={value}
                min={min}
                max={max}
                disabled={disabled}
                aria-describedby={helpId}
                onChange={(event) => onChange(event.target.value)}
            />
            <Form.Text id={helpId} as="div" muted>
                {`The last day to answer. It ends at 11:59 PM Mountain time. ${emptyMeans}`}
            </Form.Text>
        </Form.Group>
    );
}

// What a refused write says inside the dialog. A 409 means the screen is
// stale, so it offers Reload.
function DialogError({ error, onReload }) {
    if (!error) return null;
    return (
        <Alert variant="danger" role="alert" className="mt-3 mb-0">
            <p className="mb-0">{error.message}</p>
            {error.conflict && onReload && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    onClick={onReload}
                >
                    Reload
                </Button>
            )}
        </Alert>
    );
}

// A confirm button that follows the preview lock like every other write.
function ConfirmButton({ variant = 'primary', blocked, locked, ...props }) {
    return (
        <Button
            variant={variant}
            disabled={blocked || locked}
            aria-describedby={locked ? PREVIEW_LOCK_NOTE_ID : undefined}
            {...props}
        />
    );
}

function DialogShell({ show, title, busy, onHide, children, footer, locked }) {
    const titleId = useId();
    return (
        <Modal
            show={show}
            onHide={() => {
                if (!busy) onHide();
            }}
            animation={false}
            restoreFocus
            centered
            aria-labelledby={titleId}
        >
            <Modal.Header closeButton={!busy}>
                <Modal.Title as="h2" className="h5" id={titleId}>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>{children}</Modal.Body>
            <Modal.Footer className="flex-column align-items-stretch flex-sm-row align-items-sm-center">
                <PreviewLockNote locked={locked} />
                {footer}
            </Modal.Footer>
        </Modal>
    );
}

/**
 * Takes back an offer nobody has answered. The reason is required and goes to
 * the program's coordinators only; the nominee is told the offer was
 * withdrawn and never sees it.
 */
export function TakeBackDialog({
    show,
    name,
    busy,
    error,
    onConfirm,
    onHide,
    onReload,
}) {
    const id = useId();
    const locked = usePreviewLock();
    const [reason, setReason] = useState('');
    useEffect(() => {
        if (show) setReason('');
    }, [show]);
    const trimmed = reason.trim();
    const problem = trimmed === '' ? 'Say why you are taking it back.' : '';
    return (
        <DialogShell
            show={show}
            title={`Take back the offer to ${name}?`}
            busy={busy}
            onHide={onHide}
            locked={locked}
            footer={
                <>
                    <Button
                        variant="outline-primary"
                        onClick={onHide}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <ConfirmButton
                        variant="danger"
                        blocked={busy || problem !== ''}
                        locked={locked}
                        onClick={() => onConfirm(trimmed)}
                    >
                        Take back the offer
                    </ConfirmButton>
                </>
            }
        >
            <p>
                The place opens again and the student is emailed that the offer
                was withdrawn.
            </p>
            <Form.Group controlId={`${id}-reason`}>
                <Form.Label>Reason</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    maxLength={TAKE_BACK_REASON_MAX}
                    value={reason}
                    disabled={busy}
                    aria-describedby={`${id}-help`}
                    onChange={(event) => setReason(event.target.value)}
                />
                <Form.Text id={`${id}-help`} as="div" muted>
                    {`Only the program's coordinators see the reason. The student does not. ${reason.length.toLocaleString('en-US')} of ${TAKE_BACK_REASON_MAX} characters.`}
                </Form.Text>
            </Form.Group>
            <DialogError error={error} onReload={onReload} />
        </DialogShell>
    );
}

/**
 * Offers a place, asking for the date to answer by. With `choices` it first
 * asks whom: one radio per eligible nominee, and the confirm is blocked until
 * one is picked. `onConfirm({ respondByDay, choice })` gets the day (empty
 * for the program's default) and the picked id.
 */
export function OfferPlaceDialog({
    show,
    title,
    intro,
    confirmLabel,
    choices = null,
    min,
    max,
    busy,
    error,
    onConfirm,
    onHide,
    onReload,
}) {
    const id = useId();
    const locked = usePreviewLock();
    const [day, setDay] = useState('');
    const [choice, setChoice] = useState('');
    useEffect(() => {
        if (show) {
            setDay('');
            setChoice('');
        }
    }, [show]);
    const needsChoice = choices !== null;
    const blocked = busy || (needsChoice && choice === '');
    return (
        <DialogShell
            show={show}
            title={title}
            busy={busy}
            onHide={onHide}
            locked={locked}
            footer={
                <>
                    <Button
                        variant="outline-primary"
                        onClick={onHide}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <ConfirmButton
                        blocked={blocked}
                        locked={locked}
                        onClick={() => onConfirm({ respondByDay: day, choice })}
                    >
                        {confirmLabel}
                    </ConfirmButton>
                </>
            }
        >
            {intro && <p>{intro}</p>}
            {needsChoice && (
                <fieldset className="mb-3">
                    <legend className="h6">
                        Who should be offered the place
                    </legend>
                    {choices.map((option) => (
                        <Form.Check
                            key={option.id}
                            type="radio"
                            name={`${id}-choice`}
                            id={`${id}-choice-${option.id}`}
                            className="mb-1 py-1"
                            label={option.label}
                            checked={choice === option.id}
                            disabled={busy}
                            onChange={() => setChoice(option.id)}
                        />
                    ))}
                </fieldset>
            )}
            <AnswerByField
                id={`${id}-day`}
                value={day}
                onChange={setDay}
                min={min}
                max={max}
                disabled={busy}
            />
            <DialogError error={error} onReload={onReload} />
        </DialogShell>
    );
}

/** Confirms giving an expired offer's place back to the students who applied. */
export function ReleaseToPoolDialog({
    show,
    name,
    busy,
    error,
    onConfirm,
    onHide,
    onReload,
}) {
    const locked = usePreviewLock();
    return (
        <DialogShell
            show={show}
            title="Release the place to applicants?"
            busy={busy}
            onHide={onHide}
            locked={locked}
            footer={
                <>
                    <Button
                        variant="outline-primary"
                        onClick={onHide}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <ConfirmButton
                        variant="danger"
                        blocked={busy}
                        locked={locked}
                        onClick={onConfirm}
                    >
                        Release to applicants
                    </ConfirmButton>
                </>
            }
        >
            <p className="mb-0">
                {`The offer to ${name} ends for good and the place opens to students who applied to this position.`}
            </p>
            <DialogError error={error} onReload={onReload} />
        </DialogShell>
    );
}

/**
 * Asks which other candidate should get the place of an expired offer. Next
 * hands the picked id on; the offer dialog for that candidate follows.
 */
export function ChooseCandidateDialog({ show, name, choices, onNext, onHide }) {
    const id = useId();
    const [choice, setChoice] = useState('');
    useEffect(() => {
        if (show) setChoice('');
    }, [show]);
    return (
        <DialogShell
            show={show}
            title="Offer the place to another candidate"
            busy={false}
            onHide={onHide}
            locked={false}
            footer={
                <>
                    <Button variant="outline-primary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={choice === ''}
                        onClick={() => onNext(choice)}
                    >
                        Next
                    </Button>
                </>
            }
        >
            <p>
                {`${name || 'The first candidate'} keeps no hold on the place once you make this offer. You write the offer on the next screen.`}
            </p>
            <fieldset>
                <legend className="h6">Who should be offered the place</legend>
                {choices.map((option) => (
                    <Form.Check
                        key={option.id}
                        type="radio"
                        name={`${id}-choice`}
                        id={`${id}-choice-${option.id}`}
                        className="mb-1 py-1"
                        label={option.label}
                        checked={choice === option.id}
                        onChange={() => setChoice(option.id)}
                    />
                ))}
            </fieldset>
        </DialogShell>
    );
}
